/**
 * useFeatureFlag Hook
 * 
 * Provides access to subscription tier, features, and limits.
 * Fetches from GET /api/organizations/me/subscription
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import api from '../lib/api';
import { useAuth } from '../contexts/AuthContext';


export function useFeatureFlag() {
  const { user } = useAuth();
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchSubscription = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.get(`/organizations/me/subscription`);
      setSubscription(response.data);
    } catch (err) {
      console.error('Failed to fetch subscription:', err);
      setError(err);
      // Default to free tier on error
      setSubscription({
        tier: 'free',
        features: [],
        limits: { pilots: 1, aircraft: 2, batteries: 2, missions: 10, documents: 5 },
        usage: {},
        all_features: {}
      });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (user) {
      fetchSubscription();
    }
  }, [user, fetchSubscription]);

  /**
   * Check if a feature is available in the current subscription
   */
  const hasFeature = useCallback((featureName) => {
    if (!subscription) return false;
    
    // Platform admins have all features
    if (subscription.is_platform_admin) return true;
    
    // Enterprise tier has all features
    if (subscription.tier === 'enterprise') return true;
    
    return subscription.features?.includes(featureName) || false;
  }, [subscription]);

  /**
   * Check if user can add more of a resource
   */
  const canAdd = useCallback((resource) => {
    if (!subscription) return false;
    
    // Platform admins bypass limits
    if (subscription.is_platform_admin) return true;
    
    const limit = subscription.limits?.[resource];
    const usage = subscription.usage?.[resource] || 0;
    
    // null means unlimited
    if (limit === null) return true;
    
    return usage < limit;
  }, [subscription]);

  /**
   * Get usage info for a resource
   */
  const getUsage = useCallback((resource) => {
    if (!subscription) return { current: 0, limit: null, remaining: null };
    
    const limit = subscription.limits?.[resource];
    const current = subscription.usage?.[resource] || 0;
    
    return {
      current,
      limit,
      remaining: limit !== null ? Math.max(0, limit - current) : null,
      isUnlimited: limit === null,
      isAtLimit: limit !== null && current >= limit
    };
  }, [subscription]);

  /**
   * Get the required tier for a feature
   */
  const getRequiredTier = useCallback((featureName) => {
    if (!subscription?.all_features) return 'professional';
    
    // Check tier order
    const tierOrder = ['free', 'professional', 'enterprise'];
    
    for (const tier of tierOrder) {
      // Free tier has no features
      if (tier === 'free') continue;
      
      // Professional has specific features
      if (tier === 'professional') {
        const proFeatures = [
          'checkmate', 'sora', 'ai_document_generation', 'document_review',
          'ucl_submissions', 'document_storage', 'scenarios',
          'insurance_tracking', 'authorizations', 'bulletins', 'reports'
        ];
        if (proFeatures.includes(featureName)) return 'professional';
      }
      
      // Enterprise has all
      if (tier === 'enterprise') return 'enterprise';
    }
    
    return 'professional';
  }, [subscription]);

  const tier = subscription?.tier || 'free';
  const tierInfo = subscription?.tier_info || {};
  const limits = subscription?.limits || {};
  const usage = subscription?.usage || {};
  const features = subscription?.features || [];
  const allFeatures = subscription?.all_features || {};
  const isPlatformAdmin = subscription?.is_platform_admin || false;

  return {
    // Core state
    tier,
    tierInfo,
    limits,
    usage,
    features,
    allFeatures,
    isPlatformAdmin,
    loading,
    error,
    
    // Helper functions
    hasFeature,
    canAdd,
    getUsage,
    getRequiredTier,
    refetch: fetchSubscription,
    
    // Derived state
    isFreeTier: tier === 'free',
    isProfessional: tier === 'professional',
    isEnterprise: tier === 'enterprise' || isPlatformAdmin
  };
}

export default useFeatureFlag;
